import * as THREE from 'three';

/** 暖色发光精灵用的径向渐变（白色，颜色由材质 color 决定） */
export function createGlowTexture(size = 128): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d')!;
  const r = size / 2;
  const grad = g.createRadialGradient(r, r, 0, r, r, r);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.18, 'rgba(255,255,255,0.75)');
  grad.addColorStop(0.45, 'rgba(255,255,255,0.25)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

// ── 昼夜（spec §6）：白天 6–17、黄昏 17–19、夜晚 19–6；着色器整体调色（色温 + 亮度），切换过渡 2 秒 ──

export type DayPhase = 'day' | 'dusk' | 'night';

export function dayPhaseOf(hour: number): DayPhase {
  const h = ((hour % 24) + 24) % 24;
  if (h >= 6 && h < 17) return 'day';
  if (h >= 17 && h < 19) return 'dusk';
  return 'night';
}

export type Grade = { tint: THREE.Color; bright: number; sat: number; glow: number };

export const GRADES: Record<DayPhase, Grade> = {
  day: { tint: new THREE.Color(1, 1, 1), bright: 1, sat: 1, glow: 0.55 },
  dusk: { tint: new THREE.Color(1.1, 0.86, 0.66), bright: 0.9, sat: 1.06, glow: 0.85 },
  night: { tint: new THREE.Color(0.56, 0.64, 1.0), bright: 0.6, sat: 0.72, glow: 1.35 },
};

export const GRADE_FADE_MS = 2000;

/** 当前调色，按 2 秒在两档之间插值 */
export class DayNight {
  phase: DayPhase;
  readonly cur: Grade;
  private from: Grade;
  private to: Grade;
  private k = 1;

  constructor(hour: number) {
    this.phase = dayPhaseOf(hour);
    const g = GRADES[this.phase];
    this.cur = { tint: g.tint.clone(), bright: g.bright, sat: g.sat, glow: g.glow };
    this.from = g;
    this.to = g;
  }

  /** 每帧调用；时段变化时开始 2 秒过渡 */
  update(dtMs: number, hour: number): void {
    const p = dayPhaseOf(hour);
    if (p !== this.phase) {
      this.phase = p;
      this.from = { ...this.cur, tint: this.cur.tint.clone() };
      this.to = GRADES[p];
      this.k = 0;
    }
    if (this.k >= 1) return;
    this.k = Math.min(1, this.k + dtMs / GRADE_FADE_MS);
    const e = this.k * this.k * (3 - 2 * this.k);
    this.cur.tint.copy(this.from.tint).lerp(this.to.tint, e);
    this.cur.bright = this.from.bright + (this.to.bright - this.from.bright) * e;
    this.cur.sat = this.from.sat + (this.to.sat - this.from.sat) * e;
    this.cur.glow = this.from.glow + (this.to.glow - this.from.glow) * e;
  }

  get transitioning(): boolean {
    return this.k < 1;
  }
}

/** 片元着色器里用的调色函数（uniform gradeTint / gradeBright / gradeSat） */
export const GRADE_GLSL = /* glsl */ `
uniform vec3 gradeTint;
uniform float gradeBright;
uniform float gradeSat;
vec3 applyGrade(vec3 c) {
  float l = dot(c, vec3(0.299, 0.587, 0.114));
  c = mix(vec3(l), c, gradeSat);
  return c * gradeTint * gradeBright;
}`;

export function gradeUniforms(): Record<string, THREE.IUniform> {
  return { gradeTint: { value: new THREE.Color(1, 1, 1) }, gradeBright: { value: 1 }, gradeSat: { value: 1 } };
}

export function setGradeUniforms(u: Record<string, THREE.IUniform>, g: Grade): void {
  (u.gradeTint.value as THREE.Color).copy(g.tint);
  u.gradeBright.value = g.bright;
  u.gradeSat.value = g.sat;
}
